/**
 * Offline fixture for the parts of the workflow constructor the Workflows API
 * (`Gasbi.Workflows.Api`) does not serve yet: board cards, their history,
 * transition requests and step form values, the lookups and the process
 * SLA. Definitions, versions, transition rules, automations and the board
 * layout come from the API, and `workflowBuilderApi.ts` mirrors every version it
 * reads into this fixture (`mirrorWorkflowVersion`) so the mocked cards move,
 * wait for approval and run automations against the real workflows. Imported
 * for its side effect from `workflowBuilderApi.ts`; delete this file once the
 * rest ships.
 *
 * The matchers have to cover absolute URLs: there is one axios instance for the
 * whole CRM client, but `axiosBaseQuery` sends `baseUrl + url`, and this api's
 * baseUrl is the full `VITE_WORKFLOWS_API_URL`. Matching on the `/elsa/api`
 * suffix keeps it working for both the dev-azure and the localhost value.
 *
 * The fixture is backed by `localStorage` on purpose: saving a structure,
 * creating a workflow and dragging a card all write back into it, so the whole
 * constructor behaves like a real backend across page reloads. Clear
 * `WORKFLOW_BUILDER_MOCK_CACHE_KEY` (or bump its `:vN` suffix, which is what a
 * change to the seed shape calls for) to start over from the seed.
 *
 * Several features attach their own adapter to the same axios instance; each
 * one chains to the previous through the trailing `.onAny().passThrough()`, so
 * this file must keep its single adapter and that final matcher.
 */
import AxiosMockAdapter from 'axios-mock-adapter';

import { RootRoleId } from '@/features/roles/types';
import instance from '@/utils/http';

import { DEFAULT_DRAFT_STATUSES, DEFAULT_DRAFT_TRANSITIONS } from '../defaults';
import {
  ApprovalMode,
  ApproverOptions,
  ApproverType,
  AutomationActionKind,
  AutomationAfterExecution,
  AutomationConditionKind,
  AutomationEmailTemplate,
  AutomationField,
  AutomationRecipient,
  AutomationRule,
  AutomationTriggerKind,
  CreationMode,
  ProcessSlaSettings,
  RestrictionMode,
  StatusCategory,
  StatusColor,
  StepForm,
  StepFormDictionary,
  StepFormFieldType,
  StepFormValues,
  TransitionInputField,
  TransitionRestriction,
  TransitionRules,
  TransitionValidator,
  Workflow,
  WorkflowApiErrorResponse,
  WorkflowAuthor,
  WorkflowEntityEventType,
  WorkflowEntityHistoryItem,
  WorkflowEntityItem,
  WorkflowErrorCode,
  WorkflowKind,
  WorkflowStatus,
  WorkflowTransition,
  WorkflowVersion,
  WorkflowVersionOrigin,
  WorkflowVersionState,
} from '../types';
import {
  MockTransitionRequest,
  findTransition,
  requiresApproval,
  toTransitionRequest,
} from './approvals';
import { runAutomationRules } from './automationEngine';

export const WORKFLOW_BUILDER_MOCK_CACHE_KEY =
  'gasbi:crm:workflow-builder-mock:v13';

/**
 * The fixture keeps every version whole; `GET /workflows/{id}` answers with
 * their summaries only and `GET /versions/{versionId}` with one of them.
 */
type MockWorkflow = Omit<Workflow, 'versions'> & {
  versions: WorkflowVersion[];
};

/** Who the fixture records as the author of a change made in the browser. */
const MOCK_CURRENT_AUTHOR: WorkflowAuthor = {
  id: 'mock-current-user',
  name: 'Поточний користувач',
};

interface MockDb {
  workflows: MockWorkflow[];
  /** Board cards keyed by workflow id. */
  entities: Record<string, WorkflowEntityItem[]>;
  /** Change history keyed by card id, oldest first. */
  history: Record<string, WorkflowEntityHistoryItem[]>;
  /** Transition rules keyed by version id, then by transition key. */
  transitionRules: Record<string, Record<string, TransitionRules>>;
  /** Global automation rules keyed by version id, in run order. */
  automationRules: Record<string, AutomationRule[]>;
  /** The process SLA keyed by version id; a version missing here has none. */
  slaSettings: Record<string, ProcessSlaSettings>;
  /**
   * What each card last filled into a step form, keyed by card id, then by the
   * status the form belongs to.
   */
  stepFormValues: Record<string, Record<string, StepFormValues>>;
  /**
   * Each card's open transition request, keyed by card id. A status change
   * closes it.
   */
  transitionRequests: Record<string, MockTransitionRequest>;
}

/** Stands in for the roles, departments and users the backend will look up. */
const APPROVER_OPTIONS: ApproverOptions = {
  roles: [
    {
      type: ApproverType.Role,
      id: 'role-case-manager',
      name: 'Менеджер справи',
    },
    {
      type: ApproverType.Role,
      id: 'role-security-officer',
      name: 'Security Officer',
    },
    { type: ApproverType.Role, id: 'role-lawyer', name: 'Юрист (Legal)' },
    { type: ApproverType.Role, id: 'role-financier', name: 'Фінансист' },
    {
      type: ApproverType.Role,
      id: 'role-head-of-unit',
      name: 'Керівник відділу',
    },
  ],
  departments: [
    {
      type: ApproverType.Department,
      id: 'dep-security',
      name: 'Служба безпеки',
    },
    {
      type: ApproverType.Department,
      id: 'dep-legal',
      name: 'Юридичний відділ',
    },
    {
      type: ApproverType.Department,
      id: 'dep-accounting',
      name: 'Бухгалтерія',
    },
  ],
  users: [
    {
      type: ApproverType.User,
      id: 'user-sydorenko',
      name: 'Сидоренко Віктор Вікторович',
      position: 'Керівник СБ',
    },
    {
      type: ApproverType.User,
      id: 'user-kovalenko',
      name: 'Коваленко Ігор Ігорьович',
      position: 'Головний юрист',
    },
  ],
};

/** Stands in for the dictionaries the backend will offer. */
const DICTIONARY_OPTIONS: StepFormDictionary[] = [
  {
    id: 'dict-counterparties',
    name: 'Контрагенти',
    items: [
      { value: 'cp-promgaz', label: 'ТОВ «Промгаз»' },
      { value: 'cp-teplodar', label: 'ПП «Теплодар»' },
      { value: 'cp-skhid-energo', label: 'ТОВ «Схід-Енерго»' },
    ],
  },
  {
    id: 'dict-contract-types',
    name: 'Типи договорів',
    items: [
      { value: 'supply', label: 'Постачання' },
      { value: 'distribution', label: 'Розподіл' },
      { value: 'transit', label: 'Транспортування' },
    ],
  },
  {
    id: 'dict-currencies',
    name: 'Валюти',
    items: [
      { value: 'UAH', label: 'Гривня' },
      { value: 'EUR', label: 'Євро' },
      { value: 'USD', label: 'Долар США' },
    ],
  },
];

/** The seeded «Виконано» form, shaped after the Figma example. */
const DEMO_STEP_FORM: StepForm = {
  fields: [
    {
      id: 'sf-1',
      key: 'deal_amount',
      label: 'Сума угоди, грн',
      type: StepFormFieldType.Number,
      isRequired: true,
      filledByRoleId: 'role-case-manager',
      visibleToRoleIds: [],
    },
    {
      id: 'sf-2',
      key: 'counterparty_ref',
      label: 'Контрагент (з довідника)',
      type: StepFormFieldType.PredefinedList,
      isRequired: true,
      filledByRoleId: 'role-case-manager',
      visibleToRoleIds: ['role-security-officer', 'role-lawyer'],
      dictionaryId: 'dict-counterparties',
    },
    {
      id: 'sf-3',
      key: 'sb_comment',
      label: 'Коментар СБ',
      type: StepFormFieldType.Textarea,
      isRequired: true,
      filledByRoleId: 'role-security-officer',
      visibleToRoleIds: ['role-security-officer'],
    },
    {
      id: 'sf-4',
      key: 'sb_check_date',
      label: 'Дата перевірки',
      type: StepFormFieldType.Date,
      isRequired: false,
      filledByRoleId: 'role-security-officer',
      visibleToRoleIds: [],
    },
  ],
};

/** Stands in for the input field catalog the backend will offer. */
const INPUT_FIELD_OPTIONS: TransitionInputField[] = [
  {
    id: 'security-conclusion',
    name: 'Висновок погодження',
    description:
      'Багаторядковий текст · пишеться в card.securityConclusion · заповнює той, хто робить фінальну перевірку',
    isRequired: true,
  },
  {
    id: 'rejection-reason',
    name: 'Причина відхилення',
    description:
      'Вибір з довідника «Причини відхилення СБ» + коментар · card.rejectionReason',
    isRequired: true,
  },
  {
    id: 'security-protocol',
    name: 'Протокол перевірки',
    description: 'Файл · card.securityProtocol',
    isRequired: false,
  },
];

/** Stands in for the restriction catalog the backend will offer. */
const RESTRICTION_OPTIONS: TransitionRestriction[] = [
  {
    id: RestrictionMode.ApproversOnly,
    name: 'Лише погоджувачі цього переходу',
    description: 'Виконати перехід може лише той, хто є в списку погодження',
  },
];

/** Stands in for the validator catalog the backend will offer. */
const VALIDATOR_OPTIONS: TransitionValidator[] = [
  {
    id: 'reason-on-reject',
    name: 'Причина обовʼязкова при відхиленні',
    description:
      'Поле «Причина» заповнене, якщо результат фінальної перевірки Reject',
  },
  {
    id: 'edrpou-valid',
    name: 'ЄДРПОУ контрагента коректний',
    description: 'Код ЄДРПОУ картки проходить перевірку контрольної суми',
  },
  {
    id: 'documents-attached',
    name: 'Документи додано',
    description: 'До картки додано хоча б один документ',
  },
];

/** The history rows the fixture writes. The real contract will carry an actor. */
const historyEvent = (
  type: WorkflowEntityEventType,
  occurredAt: string,
  rest: Partial<WorkflowEntityHistoryItem> = {},
): WorkflowEntityHistoryItem => ({
  id: crypto.randomUUID(),
  type,
  occurredAt,
  ...rest,
});

const createdEvent = (occurredAt = new Date().toISOString()) =>
  historyEvent(WorkflowEntityEventType.Created, occurredAt);

const statusChangedEvent = (
  fromStatusKey: string | null,
  toStatusKey: string,
  occurredAt = new Date().toISOString(),
) =>
  historyEvent(WorkflowEntityEventType.StatusChanged, occurredAt, {
    fromStatusKey,
    toStatusKey,
  });

const renamedEvent = (
  fromTitle: string,
  toTitle: string,
  occurredAt = new Date().toISOString(),
) =>
  historyEvent(WorkflowEntityEventType.Renamed, occurredAt, {
    fromTitle,
    toTitle,
  });

const stepFormUpdatedEvent = (
  stepFormStatusKey: string,
  occurredAt = new Date().toISOString(),
) =>
  historyEvent(WorkflowEntityEventType.StepFormUpdated, occurredAt, {
    stepFormStatusKey,
  });

const requestEvent = (
  type: WorkflowEntityEventType,
  request: MockTransitionRequest,
  rest: Partial<WorkflowEntityHistoryItem> = {},
) =>
  historyEvent(type, new Date().toISOString(), {
    transitionKey: request.transitionKey,
    toStatusKey: request.toStatusKey,
    changedByDisplayName: MOCK_CURRENT_AUTHOR.name,
    ...rest,
  });

const daysAgo = (days: number) =>
  new Date(Date.now() - days * 24 * 60 * 60 * 1000).toISOString();

interface SeedVersionOptions {
  number: number;
  state: WorkflowVersionState;
  at: string;
  author: WorkflowAuthor;
  hasStatuses: boolean;
  origin?: WorkflowVersionOrigin;
}

/**
 * A finished version for the seeded list rows — only the demo workflow's board
 * is meant to be opened, so these carry the standard statuses and nothing more.
 */
const seedVersion = (
  workflowId: string,
  { number, state, at, author, hasStatuses, origin }: SeedVersionOptions,
): WorkflowVersion => ({
  id: `${workflowId}-v${number}`,
  number,
  state,
  origin: origin ?? WorkflowVersionOrigin.Human,
  createdAt: at,
  publishedAt: state === WorkflowVersionState.Draft ? null : at,
  updatedAt: at,
  updatedBy: author,
  statuses: hasStatuses
    ? DEFAULT_DRAFT_STATUSES.map((status, index) => ({
        ...status,
        id: `${workflowId}-v${number}-st-${index + 1}`,
        order: index + 1,
      }))
    : [],
  transitions: hasStatuses
    ? DEFAULT_DRAFT_TRANSITIONS.map((transition, index) => ({
        ...transition,
        id: `${workflowId}-v${number}-tr-${index + 1}`,
      }))
    : [],
});

const SAVCHENKO: WorkflowAuthor = {
  id: 'mock-author-savchenko',
  name: 'Савченко В. В',
};
const SYDORENKO: WorkflowAuthor = {
  id: 'mock-author-sydorenko',
  name: 'Сидоренко А. А',
};
const SYNKOKIN: WorkflowAuthor = {
  id: 'mock-author-synkokin',
  name: 'Синькокінь З. О',
};
const KOVALENKO: WorkflowAuthor = {
  id: 'mock-author-kovalenko',
  name: 'Коваленко Ю. Р',
};

const seedListWorkflows = (): MockWorkflow[] => [
  {
    id: 'contract-signing',
    name: 'Укладання договору',
    description: '',
    kind: WorkflowKind.Foreground,
    entityType: 'Справа договору',
    creationMode: CreationMode.Agent,
    versions: [
      seedVersion('contract-signing', {
        number: 3,
        state: WorkflowVersionState.Draft,
        at: daysAgo(2),
        author: SAVCHENKO,
        hasStatuses: true,
        origin: WorkflowVersionOrigin.Agent,
      }),
      seedVersion('contract-signing', {
        number: 2,
        state: WorkflowVersionState.Published,
        at: daysAgo(20),
        author: SAVCHENKO,
        hasStatuses: true,
        origin: WorkflowVersionOrigin.Agent,
      }),
      seedVersion('contract-signing', {
        number: 1,
        state: WorkflowVersionState.Retired,
        at: daysAgo(60),
        author: KOVALENKO,
        hasStatuses: true,
      }),
    ],
  },
  {
    id: 'debt-accrual',
    name: 'Нарахування боргу (нічний розрахунок)',
    description: '',
    kind: WorkflowKind.Background,
    entityType: null,
    creationMode: CreationMode.Manual,
    versions: [
      seedVersion('debt-accrual', {
        number: 1,
        state: WorkflowVersionState.Published,
        at: daysAgo(45),
        author: SYDORENKO,
        hasStatuses: false,
      }),
    ],
  },
  {
    id: 'meter-reading-confirmation',
    name: 'Підтвердження показань лічильника',
    description: '',
    kind: WorkflowKind.Subworkflow,
    entityType: null,
    creationMode: CreationMode.Manual,
    versions: [
      seedVersion('meter-reading-confirmation', {
        number: 1,
        state: WorkflowVersionState.Published,
        at: daysAgo(90),
        author: SYNKOKIN,
        hasStatuses: false,
      }),
    ],
  },
];

/** The demo board's statuses as published in v2 and kept by the draft v3. */
const DEMO_STATUSES: WorkflowStatus[] = [
  {
    id: 'st-1',
    key: 'to_do',
    name: 'До виконання',
    color: StatusColor.Blue,
    order: 1,
    category: StatusCategory.ToDo,
    isInitial: true,
    isFinal: false,
  },
  {
    id: 'st-2',
    key: 'in_progress',
    name: 'У роботі',
    color: StatusColor.Yellow,
    order: 2,
    category: StatusCategory.InProgress,
    isInitial: false,
    isFinal: false,
  },
  {
    id: 'st-3',
    key: 'done',
    name: 'Виконано',
    color: StatusColor.Teal,
    order: 3,
    category: StatusCategory.Done,
    isInitial: false,
    isFinal: true,
    stepForm: DEMO_STEP_FORM,
  },
  {
    id: 'st-4',
    key: 'wont_do',
    name: 'Не виконуватиметься',
    color: StatusColor.Purple,
    order: 4,
    category: StatusCategory.Rejected,
    isInitial: false,
    isFinal: true,
  },
];

const DEMO_TRANSITIONS: WorkflowTransition[] = [
  {
    id: 'tr-1',
    key: 'start',
    name: 'Взяти в роботу',
    fromStatusKey: 'to_do',
    toStatusKey: 'in_progress',
  },
  {
    id: 'tr-2',
    key: 'finish',
    name: 'Завершити',
    fromStatusKey: 'in_progress',
    toStatusKey: 'done',
  },
  {
    id: 'tr-3',
    key: 'reject',
    name: 'Відхилити',
    fromStatusKey: null,
    toStatusKey: 'wont_do',
  },
];

/**
 * v1 still had the lawyer's review step that v2 dropped. Cards were left in
 * it, so the board has to read statuses across versions to show them.
 */
const DEMO_ARCHIVED_STATUSES: WorkflowStatus[] = [
  DEMO_STATUSES[0],
  DEMO_STATUSES[1],
  {
    id: 'st-legal',
    key: 'legal_review',
    name: 'На перевірці юристом',
    color: StatusColor.Purple,
    order: 3,
    category: StatusCategory.InProgress,
    isInitial: false,
    isFinal: false,
  },
  { ...DEMO_STATUSES[2], order: 4, stepForm: null },
  { ...DEMO_STATUSES[3], order: 5 },
];

const DEMO_ARCHIVED_TRANSITIONS: WorkflowTransition[] = [
  DEMO_TRANSITIONS[0],
  {
    id: 'tr-legal',
    key: 'to_legal',
    name: 'На перевірку юристу',
    fromStatusKey: 'in_progress',
    toStatusKey: 'legal_review',
  },
  { ...DEMO_TRANSITIONS[1], fromStatusKey: 'legal_review' },
  DEMO_TRANSITIONS[2],
];

const EMAIL_TEMPLATE_OPTIONS: AutomationEmailTemplate[] = [
  { id: 'tpl-default', name: 'Стандартне сповіщення' },
  { id: 'tpl-status-changed', name: 'Зміна статусу справи' },
  { id: 'tpl-rejected', name: 'Справу відхилено' },
];

/** The demo's rules: one per trigger kind, one of them written by the agent. */
const demoAutomationRules = (at: string): AutomationRule[] => [
  {
    id: 'ar-1',
    isEnabled: true,
    origin: WorkflowVersionOrigin.Human,
    createdBy: SYDORENKO,
    createdAt: at,
    activeFrom: null,
    activeTo: null,
    trigger: {
      kind: AutomationTriggerKind.TransitionExecuted,
      transitionKey: null,
    },
    conditions: [],
    actions: [
      {
        id: 'aa-1',
        kind: AutomationActionKind.SendEmail,
        recipient: AutomationRecipient.CaseManager,
        templateId: 'tpl-default',
        afterExecution: AutomationAfterExecution.None,
      },
    ],
  },
  {
    id: 'ar-2',
    isEnabled: true,
    origin: WorkflowVersionOrigin.Agent,
    createdBy: null,
    createdAt: at,
    activeFrom: null,
    activeTo: null,
    trigger: {
      kind: AutomationTriggerKind.StatusEntered,
      statusKey: 'wont_do',
    },
    conditions: [
      {
        id: 'ac-1',
        kind: AutomationConditionKind.PreviousStatusIn,
        keys: ['done'],
      },
    ],
    actions: [
      {
        id: 'aa-2',
        kind: AutomationActionKind.MoveToStatus,
        statusKey: 'in_progress',
        afterExecution: AutomationAfterExecution.StopOtherRules,
      },
    ],
  },
  {
    id: 'ar-3',
    isEnabled: false,
    origin: WorkflowVersionOrigin.Human,
    createdBy: KOVALENKO,
    createdAt: at,
    activeFrom: null,
    activeTo: null,
    trigger: {
      kind: AutomationTriggerKind.StatusEntered,
      statusKey: 'wont_do',
    },
    conditions: [],
    actions: [
      {
        id: 'aa-3',
        kind: AutomationActionKind.UpdateField,
        field: AutomationField.Assignee,
        afterExecution: AutomationAfterExecution.None,
      },
    ],
  },
];

const demoApprovalRules = (): TransitionRules => ({
  approval: {
    approvers: APPROVER_OPTIONS.users,
    mode: ApprovalMode.All,
    finalCheckByLastApprover: false,
  },
  rejection: null,
  actions: null,
  validators: null,
  restrictions: null,
  input: null,
});

/**
 * «Завершити» is open to managers only (the shared RBAC «Manager» role), so
 * the restriction can be tried on the demo board right away.
 */
const demoRestrictedRules = (): TransitionRules => ({
  approval: null,
  rejection: null,
  actions: null,
  validators: null,
  restrictions: {
    mode: RestrictionMode.Audience,
    audience: [
      {
        type: ApproverType.Role,
        id: RootRoleId.Manager,
        name: 'Менеджер',
      },
    ],
  },
  input: null,
});

const seed = (): MockDb => {
  const now = new Date().toISOString();

  const demoVersion = (
    number: number,
    state: WorkflowVersionState,
    at: string,
    structure: Pick<WorkflowVersion, 'statuses' | 'transitions'>,
  ): WorkflowVersion => ({
    id: `demo-workflow-v${number}`,
    number,
    state,
    origin: WorkflowVersionOrigin.Human,
    createdAt: at,
    publishedAt: state === WorkflowVersionState.Draft ? null : at,
    updatedAt: at,
    updatedBy: KOVALENKO,
    ...structure,
  });

  const demoWorkflow: MockWorkflow = {
    id: 'demo-workflow',
    name: 'Демо-воркфлоу',
    description: '',
    kind: WorkflowKind.Foreground,
    entityType: 'Справа договору',
    creationMode: CreationMode.Manual,
    versions: [
      demoVersion(3, WorkflowVersionState.Draft, now, {
        statuses: DEMO_STATUSES,
        transitions: DEMO_TRANSITIONS,
      }),
      demoVersion(2, WorkflowVersionState.Published, daysAgo(7), {
        statuses: DEMO_STATUSES,
        transitions: DEMO_TRANSITIONS,
      }),
      demoVersion(1, WorkflowVersionState.Retired, daysAgo(30), {
        statuses: DEMO_ARCHIVED_STATUSES,
        transitions: DEMO_ARCHIVED_TRANSITIONS,
      }),
    ],
  };

  const entities: WorkflowEntityItem[] = [
    {
      id: 'e-1',
      title: 'Договір №114 — ТОВ «Промгаз»',
      statusKey: 'to_do',
      createdAt: now,
    },
    {
      id: 'e-2',
      title: 'Договір №115 — ПП «Теплодар»',
      statusKey: 'to_do',
      createdAt: now,
    },
    {
      id: 'e-3',
      title: 'Договір №116 — ТОВ «Схід-Енерго»',
      statusKey: 'in_progress',
      createdAt: now,
    },
    {
      id: 'e-4',
      title: 'Договір №117 — ФОП Левченко',
      statusKey: 'done',
      createdAt: now,
    },
    {
      id: 'e-5',
      title: 'Договір №118 — ТОВ «Газмережі»',
      statusKey: 'wont_do',
      createdAt: now,
    },
    {
      id: 'e-6',
      title: 'Договір №109 — ТОВ «Укртеплоенерго»',
      statusKey: 'legal_review',
      createdAt: daysAgo(30),
    },
    {
      id: 'e-7',
      title: 'Договір №110 — ПП «Нафтогазсервіс»',
      statusKey: 'legal_review',
      createdAt: daysAgo(30),
    },
  ];

  // Every card starts with the event that produced it; the cards that are not
  // in the initial column also carry the move that put them there, so the
  // timeline shows more than a single row out of the box.
  const history = entities.reduce<Record<string, WorkflowEntityHistoryItem[]>>(
    (acc, entity) => {
      const events: WorkflowEntityHistoryItem[] = [
        createdEvent(entity.createdAt),
      ];

      if (entity.statusKey !== 'to_do') {
        events.push(
          statusChangedEvent('to_do', entity.statusKey, entity.createdAt),
        );
      }

      return { ...acc, [entity.id]: events };
    },
    {},
  );

  return {
    workflows: [demoWorkflow, ...seedListWorkflows()],
    entities: { [demoWorkflow.id]: entities },
    history,
    // «Взяти в роботу» waits for both approvers, so the approval flow can be
    // tried on the demo board right away.
    transitionRules: {
      'demo-workflow-v3': {
        start: demoApprovalRules(),
        finish: demoRestrictedRules(),
      },
      'demo-workflow-v2': {
        start: demoApprovalRules(),
        finish: demoRestrictedRules(),
      },
    },
    automationRules: {
      'demo-workflow-v3': demoAutomationRules(now),
      'demo-workflow-v2': demoAutomationRules(daysAgo(7)),
    },
    slaSettings: {},
    stepFormValues: {},
    transitionRequests: {},
  };
};

/**
 * The session fallback: with storage unavailable the fixture still behaves like
 * a backend, it just forgets everything on reload.
 */
let memoryDb: MockDb | null = null;

const isMockDb = (value: unknown): value is MockDb =>
  !!value &&
  typeof value === 'object' &&
  Array.isArray((value as MockDb).workflows) &&
  !!(value as MockDb).entities &&
  typeof (value as MockDb).entities === 'object';

/** A cache written before a store existed still reads back fine. */
const withDefaults = (db: MockDb): MockDb => ({
  ...db,
  history: db.history ?? {},
  transitionRules: db.transitionRules ?? {},
  automationRules: db.automationRules ?? {},
  slaSettings: db.slaSettings ?? {},
  stepFormValues: db.stepFormValues ?? {},
  transitionRequests: db.transitionRequests ?? {},
});

const writeDb = (db: MockDb) => {
  memoryDb = db;

  try {
    localStorage.setItem(WORKFLOW_BUILDER_MOCK_CACHE_KEY, JSON.stringify(db));
  } catch {
    // Storage unavailable: the fixture lives in memory for this session only.
  }
};

const readDb = (): MockDb => {
  try {
    const raw = localStorage.getItem(WORKFLOW_BUILDER_MOCK_CACHE_KEY);
    const parsed: unknown = raw ? JSON.parse(raw) : null;

    if (isMockDb(parsed)) return withDefaults(parsed);
  } catch {
    // Storage unavailable or the stored value is not ours: fall through.
  }

  const db = memoryDb ?? seed();

  writeDb(db);
  return db;
};

/** `/workflows/{workflowId}/versions/{versionId}`, optionally followed by more. */
const parseVersionIds = (url = '') => {
  const match = /\/elsa\/api\/workflows\/([^/?]+)\/versions\/([^/?]+)/.exec(
    url,
  );

  return { workflowId: match?.[1] ?? '', versionId: match?.[2] ?? '' };
};

const findVersion = (db: MockDb, workflowId: string, versionId: string) =>
  db.workflows
    .find(item => item.id === workflowId)
    ?.versions.find(version => version.id === versionId);

const appendEvent = (
  db: MockDb,
  entityId: string,
  event: WorkflowEntityHistoryItem,
) => {
  db.history[entityId] = [...(db.history[entityId] ?? []), event];
};

/** Keeps the latest fill of a step form; the next entry is prefilled from it. */
const saveStepFormValues = (
  db: MockDb,
  entityId: string,
  statusKey: string,
  formValues?: StepFormValues,
) => {
  if (!formValues) return;

  db.stepFormValues[entityId] = {
    ...db.stepFormValues[entityId],
    [statusKey]: formValues,
  };
};

/** `/workflows/{workflowId}` and, for the status route, `/entities/{entityId}`. */
const parseIds = (url = '') => {
  // Anchored on `/elsa/api/` like the matchers: the relative baseUrl
  // `/workflows/elsa/api` itself contains `/workflows/`, which an unanchored
  // pattern would read as the workflow id `elsa`.
  const match =
    /\/elsa\/api\/workflows\/([^/?]+)(?:\/entities\/([^/?]+))?/.exec(url);

  return { workflowId: match?.[1], entityId: match?.[2] };
};

/**
 * Every failure of the real contract is an HTTP 400 carrying this body — there
 * is no 404.
 */
const errorBody = (
  errorCode: WorkflowErrorCode,
  errorMessage: string,
  propertyName?: string,
): WorkflowApiErrorResponse => ({
  status: 400,
  title: 'Bad Request',
  traceId: crypto.randomUUID(),
  errors: [
    { errorCode, errorMessage, ...(propertyName ? { propertyName } : {}) },
  ],
});

/** The version that runs — its transitions and rules govern the cards. */
const findPublished = (db: MockDb, workflowId: string) => {
  const workflow = db.workflows.find(item => item.id === workflowId);

  return workflow && findByState(workflow, WorkflowVersionState.Published);
};

/** The card as the contract returns it — with its open request summed up. */
const withPendingTransition = (
  db: MockDb,
  entity: WorkflowEntityItem,
): WorkflowEntityItem => {
  const request = db.transitionRequests[entity.id];

  if (!request) return { ...entity, pendingTransition: null };

  const { toStatusKey, approvedCount, requiredCount } =
    toTransitionRequest(request);

  return {
    ...entity,
    pendingTransition: { toStatusKey, approvedCount, requiredCount },
  };
};

/** The published transition into `toStatusKey` and its approval rule. */
const resolveMove = (
  db: MockDb,
  workflowId: string,
  entity: WorkflowEntityItem,
  toStatusKey: string,
) => {
  const published = findPublished(db, workflowId);
  const transition =
    published &&
    findTransition(published.transitions, entity.statusKey, toStatusKey);
  const rule =
    published && transition
      ? db.transitionRules[published.id]?.[transition.key]?.approval
      : null;

  return {
    published,
    transition,
    rule: requiresApproval(rule) ? rule : null,
  };
};

/**
 * Why a move into `toStatusKey` is refused: the card waits for another
 * transition, or this one is not approved yet — or was never requested.
 */
const moveError = (
  db: MockDb,
  workflowId: string,
  entity: WorkflowEntityItem,
  toStatusKey: string,
) => {
  const request = db.transitionRequests[entity.id];

  if (request && request.toStatusKey !== toStatusKey) {
    return errorBody(
      WorkflowErrorCode.TransitionRequestPending,
      'Спершу скасуйте перехід, що очікує погодження',
    );
  }

  if (request) {
    return toTransitionRequest(request).isApproved
      ? null
      : errorBody(
          WorkflowErrorCode.TransitionApprovalRequired,
          'Перехід ще не погоджено',
        );
  }

  return resolveMove(db, workflowId, entity, toStatusKey).rule
    ? errorBody(
        WorkflowErrorCode.TransitionApprovalRequired,
        'Перехід потребує погодження',
      )
    : null;
};

/**
 * Moves a card the way a board drop does: the history row, the step form, the
 * published version's automation rules. The move closes the card's open
 * request.
 */
const moveEntity = (
  db: MockDb,
  workflowId: string,
  entity: WorkflowEntityItem,
  toStatusKey: string,
  formValues?: StepFormValues,
) => {
  const fromStatusKey = entity.statusKey;

  appendEvent(db, entity.id, statusChangedEvent(fromStatusKey, toStatusKey));
  entity.statusKey = toStatusKey;
  saveStepFormValues(db, entity.id, toStatusKey, formValues);
  delete db.transitionRequests[entity.id];
  applyAutomationRules(db, workflowId, entity, fromStatusKey, toStatusKey);
};

const notFound = () =>
  errorBody(WorkflowErrorCode.NotFound, 'Воркфлоу не знайдено');

const findByState = (workflow: MockWorkflow, state: WorkflowVersionState) =>
  workflow.versions.find(version => version.state === state);

/**
 * Runs the published version's automation rules after a card moved from
 * `fromStatusKey` to `toStatusKey`, applying every move they chain. Both the
 * board drop and the card dialog's status change go through it — only the
 * published board holds cards, so its version's rules are the ones that run.
 */
const applyAutomationRules = (
  db: MockDb,
  workflowId: string,
  entity: WorkflowEntityItem,
  fromStatusKey: string,
  toStatusKey: string,
) => {
  const workflow = db.workflows.find(item => item.id === workflowId);
  const published =
    workflow && findByState(workflow, WorkflowVersionState.Published);

  if (!published) return;

  const { moves } = runAutomationRules(
    db.automationRules[published.id] ?? [],
    published,
    {
      fromStatusKey,
      toStatusKey,
      stepFormValues: db.stepFormValues[entity.id] ?? {},
      today: new Date().toISOString().slice(0, 10),
    },
  );

  moves.forEach(move => {
    appendEvent(
      db,
      entity.id,
      statusChangedEvent(move.fromStatusKey, move.toStatusKey),
    );
    entity.statusKey = move.toStatusKey;
  });
};

type MockWorkflowMeta = Omit<MockWorkflow, 'versions'>;

/** What a version mirrored before its workflow was read starts with. */
const UNKNOWN_WORKFLOW_META: Omit<MockWorkflowMeta, 'id'> = {
  name: '',
  description: '',
  kind: WorkflowKind.Foreground,
  entityType: null,
  creationMode: CreationMode.Manual,
};

const upsertWorkflow = (db: MockDb, id: string, meta?: MockWorkflowMeta) => {
  const existing = db.workflows.find(item => item.id === id);

  if (existing) {
    if (meta) Object.assign(existing, meta);
    return existing;
  }

  const workflow: MockWorkflow = {
    ...UNKNOWN_WORKFLOW_META,
    ...meta,
    id,
    versions: [],
  };

  db.workflows.push(workflow);
  db.entities[id] ??= [];
  return workflow;
};

/** Copies a workflow's own fields read from the Workflows API. */
export const mirrorWorkflow = (meta: MockWorkflowMeta) => {
  const db = readDb();

  upsertWorkflow(db, meta.id, meta);
  writeDb(db);
};

/**
 * Copies a version read from the Workflows API, with its rules: the mocked
 * card endpoints read the published version, its transition rules and its
 * automations from here. Only one version stays published, as on the server.
 */
export const mirrorWorkflowVersion = (
  workflowId: string,
  version: WorkflowVersion,
  transitionRules: Record<string, TransitionRules>,
  automationRules: AutomationRule[],
) => {
  const db = readDb();
  const workflow = upsertWorkflow(db, workflowId);

  if (version.state === WorkflowVersionState.Published) {
    workflow.versions.forEach(item => {
      if (
        item.id !== version.id &&
        item.state === WorkflowVersionState.Published
      ) {
        item.state = WorkflowVersionState.Retired;
      }
    });
  }

  workflow.versions = [
    version,
    ...workflow.versions.filter(item => item.id !== version.id),
  ].sort((left, right) => right.number - left.number);
  db.transitionRules[version.id] = transitionRules;
  db.automationRules[version.id] = automationRules;
  writeDb(db);
};

/** A draft created from a published version starts with its process SLA. */
export const copyMockVersionSettings = (
  fromVersionId: string,
  toVersionId: string,
) => {
  const db = readDb();
  const sla = db.slaSettings[fromVersionId];

  if (!sla) return;

  db.slaSettings[toVersionId] = structuredClone(sla);
  writeDb(db);
};

const mock = new AxiosMockAdapter(instance, { delayResponse: 300 });

mock
  .onGet(/\/elsa\/api\/workflows\/approvers\/?(\?.*)?$/)
  .reply(() => [200, APPROVER_OPTIONS])
  .onGet(/\/elsa\/api\/workflows\/validators\/?(\?.*)?$/)
  .reply(() => [200, VALIDATOR_OPTIONS])
  .onGet(/\/elsa\/api\/workflows\/restrictions\/?(\?.*)?$/)
  .reply(() => [200, RESTRICTION_OPTIONS])
  .onGet(/\/elsa\/api\/workflows\/input-fields\/?(\?.*)?$/)
  .reply(() => [200, INPUT_FIELD_OPTIONS])
  .onGet(/\/elsa\/api\/workflows\/dictionaries\/?(\?.*)?$/)
  .reply(() => [200, DICTIONARY_OPTIONS])
  .onGet(/\/elsa\/api\/workflows\/email-templates\/?(\?.*)?$/)
  .reply(() => [200, EMAIL_TEMPLATE_OPTIONS])
  .onGet(
    /\/elsa\/api\/workflows\/[^/]+\/versions\/[^/]+\/sla-settings\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { workflowId, versionId } = parseVersionIds(config.url);

    if (!findVersion(db, workflowId, versionId)) return [400, notFound()];

    return [200, db.slaSettings[versionId] ?? { sla: null }];
  })

  .onGet(
    /\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/step-forms\/[^/?]+\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { entityId = '' } = parseIds(config.url);
    const statusKey =
      /\/step-forms\/([^/?]+)/.exec(config.url ?? '')?.[1] ?? '';

    return [200, db.stepFormValues[entityId]?.[statusKey] ?? {}];
  })
  .onGet(
    /\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/transition-request\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { entityId = '' } = parseIds(config.url);
    const request = db.transitionRequests[entityId];

    return [200, request ? toTransitionRequest(request) : null];
  })
  .onGet(/\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/history\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { entityId = '' } = parseIds(config.url);

    return [200, db.history[entityId] ?? []];
  })
  .onGet(/\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { workflowId = '', entityId } = parseIds(config.url);
    const entity = db.entities[workflowId]?.find(item => item.id === entityId);

    if (!entity) {
      return [400, errorBody(WorkflowErrorCode.NotFound, 'Картку не знайдено')];
    }

    return [200, withPendingTransition(db, entity)];
  })
  .onGet(/\/elsa\/api\/workflows\/[^/]+\/entities\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { workflowId = '' } = parseIds(config.url);

    return [
      200,
      (db.entities[workflowId] ?? []).map(entity =>
        withPendingTransition(db, entity),
      ),
    ];
  })
  .onPost(
    /\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/transition-request\/approve\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { workflowId = '', entityId } = parseIds(config.url);
    const { approverId } = JSON.parse(config.data) as { approverId: string };
    const entity = db.entities[workflowId]?.find(item => item.id === entityId);
    const request = entity && db.transitionRequests[entity.id];

    if (!entity || !request) {
      return [
        400,
        errorBody(WorkflowErrorCode.NotFound, 'Запит на перехід не знайдено'),
      ];
    }

    const current = toTransitionRequest(request);
    const slot = current.approvers.find(
      item => item.approver.id === approverId,
    );

    if (!slot || slot.decidedAt || !current.canApprove) {
      return [
        400,
        errorBody(
          WorkflowErrorCode.TransitionApprovalRequired,
          'Цей погоджувач не може погодити перехід',
        ),
      ];
    }

    request.decisions.push({
      approverId,
      decidedAt: new Date().toISOString(),
      decidedByDisplayName: MOCK_CURRENT_AUTHOR.name,
    });
    appendEvent(
      db,
      entity.id,
      requestEvent(WorkflowEntityEventType.Approved, request, {
        approverName: slot.approver.name,
      }),
    );

    const updated = toTransitionRequest(request);

    // Nobody has to check or fill anything, so the last approval moves it.
    if (updated.isApproved && !updated.awaitingMove) {
      moveEntity(db, workflowId, entity, updated.toStatusKey);
      writeDb(db);
      return [200, null];
    }

    writeDb(db);
    return [200, updated];
  })
  .onPost(
    /\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/transition-request\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { workflowId = '', entityId } = parseIds(config.url);
    const { toStatusKey } = JSON.parse(config.data) as { toStatusKey: string };
    const entity = db.entities[workflowId]?.find(item => item.id === entityId);

    if (!entity) {
      return [400, errorBody(WorkflowErrorCode.NotFound, 'Картку не знайдено')];
    }

    if (db.transitionRequests[entity.id]) {
      return [
        400,
        errorBody(
          WorkflowErrorCode.TransitionRequestPending,
          'Картка вже очікує погодження переходу',
        ),
      ];
    }

    const { published, transition, rule } = resolveMove(
      db,
      workflowId,
      entity,
      toStatusKey,
    );

    if (!published || !transition || !rule) {
      return [
        400,
        errorBody(WorkflowErrorCode.NotFound, 'Перехід не потребує погодження'),
      ];
    }

    const target = published.statuses.find(
      status => status.key === toStatusKey,
    );
    const request: MockTransitionRequest = {
      id: crypto.randomUUID(),
      transitionKey: transition.key,
      toStatusKey,
      rule,
      decisions: [],
      createdAt: new Date().toISOString(),
      createdByDisplayName: MOCK_CURRENT_AUTHOR.name,
      targetHasStepForm: !!target?.stepForm?.fields.length,
    };

    db.transitionRequests[entity.id] = request;
    appendEvent(
      db,
      entity.id,
      requestEvent(WorkflowEntityEventType.TransitionRequested, request),
    );
    writeDb(db);

    return [200, toTransitionRequest(request)];
  })
  .onPost(/\/elsa\/api\/workflows\/[^/]+\/entities\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { workflowId = '' } = parseIds(config.url);
    const { title, statusKey } = JSON.parse(config.data) as {
      title: string;
      statusKey: string;
    };
    const id = crypto.randomUUID();

    const createdAt = new Date().toISOString();

    db.entities[workflowId] = [
      ...(db.entities[workflowId] ?? []),
      { id, title, statusKey, createdAt },
    ];
    appendEvent(db, id, createdEvent(createdAt));
    writeDb(db);

    return [201, { id }];
  })

  .onPut(
    /\/elsa\/api\/workflows\/[^/]+\/versions\/[^/]+\/sla-settings\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { workflowId, versionId } = parseVersionIds(config.url);
    const version = findVersion(db, workflowId, versionId);

    if (!version) return [400, notFound()];

    // A published or archived version is frozen, its settings with it.
    if (version.state !== WorkflowVersionState.Draft) {
      return [
        400,
        errorBody(
          WorkflowErrorCode.HasNoDraft,
          'Налаштування редагуються лише в чернетці',
        ),
      ];
    }

    const { sla } = JSON.parse(config.data) as ProcessSlaSettings;

    db.slaSettings[versionId] = { sla };
    writeDb(db);

    return [204];
  })
  .onPut(/\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/status\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { workflowId = '', entityId } = parseIds(config.url);
    const { toStatusKey, formValues } = JSON.parse(config.data) as {
      toStatusKey: string;
      formValues?: StepFormValues;
    };
    const entity = db.entities[workflowId]?.find(item => item.id === entityId);

    if (!entity) {
      return [400, errorBody(WorkflowErrorCode.NotFound, 'Картку не знайдено')];
    }

    const blocked = moveError(db, workflowId, entity, toStatusKey);

    if (blocked) return [400, blocked];

    moveEntity(db, workflowId, entity, toStatusKey, formValues);
    writeDb(db);

    return [204];
  })
  .onPut(/\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/?(\?.*)?$/)
  .reply(config => {
    const db = readDb();
    const { workflowId = '', entityId } = parseIds(config.url);
    const { title, statusKey, formValues, stepFormValues } = JSON.parse(
      config.data,
    ) as {
      title: string;
      statusKey: string;
      formValues?: StepFormValues;
      stepFormValues?: Record<string, StepFormValues>;
    };
    const entity = db.entities[workflowId]?.find(item => item.id === entityId);

    if (!entity) {
      return [400, errorBody(WorkflowErrorCode.NotFound, 'Картку не знайдено')];
    }

    const blocked =
      statusKey !== entity.statusKey &&
      moveError(db, workflowId, entity, statusKey);

    if (blocked) return [400, blocked];

    // Only a field that actually changed earns a history row — the dialog
    // submits both every time.
    if (title !== entity.title) {
      appendEvent(db, entity.id, renamedEvent(entity.title, title));
      entity.title = title;
    }

    const fromStatusKey = entity.statusKey;
    const statusChanged = statusKey !== fromStatusKey;

    if (statusChanged) {
      appendEvent(db, entity.id, statusChangedEvent(fromStatusKey, statusKey));
      entity.statusKey = statusKey;
      saveStepFormValues(db, entity.id, statusKey, formValues);
      delete db.transitionRequests[entity.id];
    }

    // Edits to forms filled earlier merge into them, one history row each.
    Object.entries(stepFormValues ?? {}).forEach(([key, values]) => {
      saveStepFormValues(db, entity.id, key, {
        ...db.stepFormValues[entity.id]?.[key],
        ...values,
      });
      appendEvent(db, entity.id, stepFormUpdatedEvent(key));
    });

    // After the form merge, so the rules' conditions see the edited values.
    if (statusChanged) {
      applyAutomationRules(db, workflowId, entity, fromStatusKey, statusKey);
    }

    writeDb(db);

    return [204];
  })
  .onDelete(
    /\/elsa\/api\/workflows\/[^/]+\/entities\/[^/]+\/transition-request\/?(\?.*)?$/,
  )
  .reply(config => {
    const db = readDb();
    const { entityId = '' } = parseIds(config.url);
    const request = db.transitionRequests[entityId];

    if (!request) {
      return [
        400,
        errorBody(WorkflowErrorCode.NotFound, 'Запит на перехід не знайдено'),
      ];
    }

    delete db.transitionRequests[entityId];
    appendEvent(
      db,
      entityId,
      requestEvent(WorkflowEntityEventType.TransitionCancelled, request),
    );
    writeDb(db);

    return [204];
  })
  .onAny()
  .passThrough();
